/**
 * kit45 — shared primitives for the four गोत्र 45-second style versions.
 *
 * Encodes the laws from research_motion/01_vox_craft.md. Read BEATS_45.md before authoring.
 *
 *  L1  graphics animate ON TWOS (12fps inside 24fps) — use useStep(), never useCurrentFrame()
 *  L2  the ground is always a MATERIAL. never empty, never pure white
 *  L3  type is a PUNCH WORD (2-5 words, on the syllable) or a LABEL on the evidence. never a subtitle
 *  L4  one accent colour = "look here now"
 *  L5  ⛔ DEVANAGARI: no letter-spacing, no per-glyph animation. animate the WHOLE WORD or a MASK
 *      over it. line-height 1.5-1.7, generous padding or the matras clip.
 *  L6  camera velocity peaks ON the cut
 *  L7  artifact first, narration second
 */
import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Easing,
  random,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: devaBase } = loadDeva("normal", {
  weights: ["400", "500", "600", "700", "800", "900"],
  subsets: ["devanagari", "latin"],
});
const { fontFamily: interBase } = loadInter("normal", {
  weights: ["400", "500", "700", "800", "900"],
  subsets: ["latin"],
});

/** Devanagari-first stack. ⛔ never set Devanagari in `inter` — it renders as tofu. */
export const deva = `${devaBase}, ${interBase}, sans-serif`;
/** Latin ONLY. */
export const inter = interBase;

/** L5: Devanagari needs its own vertical rhythm or matras clip. */
export const devaBlock: React.CSSProperties = {
  fontFamily: deva,
  lineHeight: 1.58,
  paddingTop: "0.22em",
  paddingBottom: "0.16em",
  letterSpacing: 0, // ⛔ never nonzero — it shears the shirorekha
};

// ── L1 · ON TWOS ────────────────────────────────────────────────────────────
/** Quantise to every 2nd frame so the graphics layer reads 12fps inside 24fps. */
export const useStep = (): number => Math.floor(useCurrentFrame() / 2) * 2;
/** Unquantised — for camera moves and anything photographic that must stay smooth. */
export const useSmooth = (): number => useCurrentFrame();

/** seconds → frames, at the composition's own fps */
export const useF = () => {
  const { fps } = useVideoConfig();
  return React.useCallback((s: number) => Math.round(s * fps), [fps]);
};

// ── timing helpers ──────────────────────────────────────────────────────────
/** Spring that fires at absolute second `at`. Stepped by default (L1). */
export const useSpringAt = (
  at: number,
  cfg: { damping?: number; stiffness?: number; mass?: number } = {},
  smooth = false,
) => {
  const { fps } = useVideoConfig();
  const frame = smooth ? useCurrentFrame() : Math.floor(useCurrentFrame() / 2) * 2;
  return spring({
    frame: frame - Math.round(at * fps),
    fps,
    config: { damping: cfg.damping ?? 14, stiffness: cfg.stiffness ?? 140, mass: cfg.mass ?? 0.9 },
  });
};

/** Keyframe a scalar over [second, value] pairs. Holds flat outside the range. */
export const useKeys = (
  keys: [number, number][],
  easing: (t: number) => number = Easing.inOut(Easing.cubic),
  smooth = false,
): number => {
  const { fps } = useVideoConfig();
  const frame = smooth ? useCurrentFrame() : Math.floor(useCurrentFrame() / 2) * 2;
  return interpolate(
    frame,
    keys.map((k) => Math.round(k[0] * fps)),
    keys.map((k) => k[1]),
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing },
  );
};

/** 0 before `at`, 1 after, with a short stepped ramp. For hard state flips. */
export const useFlag = (at: number, ramp = 0.12): number =>
  useKeys([
    [at, 0],
    [at + ramp, 1],
  ], Easing.linear);

// ── L6 · CAMERA ─────────────────────────────────────────────────────────────
/**
 * A 2D camera whose velocity peaks ON the cut. Wrap a whole plate in it.
 * `moves`: [second, x, y, scale][] — held flat between entries, eased across.
 */
export const Camera: React.FC<{
  moves: [number, number, number, number][];
  blur?: number;
  children: React.ReactNode;
}> = ({ moves, blur = 0, children }) => {
  const { fps } = useVideoConfig();
  const frame = useCurrentFrame(); // camera stays smooth — L1 applies to graphics, not the lens
  const secs = moves.map((m) => Math.round(m[0] * fps));
  const pick = (i: 1 | 2 | 3) =>
    interpolate(frame, secs, moves.map((m) => m[i]), {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.bezier(0.76, 0, 0.24, 1), // velocity peaks in the middle of the move
    });
  const x = pick(1);
  const y = pick(2);
  const s = pick(3);

  // motion blur proxy: derivative of scale+position, ramps in fast and out faster
  const prev = Math.max(0, frame - 1);
  const at = (f: number, i: 1 | 2 | 3) =>
    interpolate(f, secs, moves.map((m) => m[i]), {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.bezier(0.76, 0, 0.24, 1),
    });
  const v =
    Math.abs(x - at(prev, 1)) + Math.abs(y - at(prev, 2)) + Math.abs(s - at(prev, 3)) * 900;
  const b = blur > 0 ? Math.min(blur, v * blur * 0.12) : 0;

  return (
    <AbsoluteFill
      style={{
        transform: `scale(${s}) translate(${x}px, ${y}px)`,
        transformOrigin: "50% 50%",
        filter: b > 0.06 ? `blur(${b.toFixed(2)}px)` : undefined,
        willChange: "transform",
      }}
    >
      {children}
    </AbsoluteFill>
  );
};

// ── PLATE gate ──────────────────────────────────────────────────────────────
/**
 * Hard-unmounts outside [from, to]. ⛔ EVERY hook must sit ABOVE this gate in a component
 * ("Rendered fewer hooks than expected" otherwise). Lead-in/out is short by design — research
 * 8c: 0.7s crossfades let two plates occupy one slot.
 */
export const Plate: React.FC<{
  from: number;
  to: number;
  lead?: number;
  tail?: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ from, to, lead = 0.3, tail = 0.35, children, style }) => {
  const { fps } = useVideoConfig();
  const frame = useCurrentFrame();
  if (frame < Math.round((from - lead) * fps) || frame >= Math.round((to + tail) * fps)) return null;
  return <AbsoluteFill style={style}>{children}</AbsoluteFill>;
};

// ── L3 · TYPE ───────────────────────────────────────────────────────────────
/**
 * PUNCH WORD — 2-5 words, lands on the spoken syllable, whole-word animation only (L5).
 * Enters with a scale punch + a mask wipe; never a fade, never per-glyph.
 */
export const Punch: React.FC<{
  at: number;
  text: string;
  size?: number;
  color?: string;
  weight?: number;
  out?: number;
  style?: React.CSSProperties;
}> = ({ at, text, size = 96, color = "#FFFFFF", weight = 800, out, style }) => {
  const s = useSpringAt(at, { damping: 12, stiffness: 190, mass: 0.75 });
  const wipe = useKeys([[at, 0], [at + 0.22, 100]], Easing.out(Easing.cubic));
  const gone = out === undefined ? 1 : useKeys([[out, 1], [out + 0.25, 0]], Easing.in(Easing.quad));
  return (
    <span
      data-audit="punch"
      style={{
        ...devaBlock,
        display: "inline-block",
        fontSize: size,
        fontWeight: weight,
        color,
        transform: `scale(${(0.86 + s * 0.14).toFixed(3)})`,
        opacity: gone,
        clipPath: `inset(-30% ${100 - wipe}% -30% -6%)`,
        whiteSpace: "pre",
        ...style,
      }}
    >
      {text}
    </span>
  );
};

/** LABEL — names the artifact. Small, quiet, says what the VO is NOT saying. */
export const Label: React.FC<{
  at: number;
  text: string;
  size?: number;
  color?: string;
  latin?: boolean;
  style?: React.CSSProperties;
}> = ({ at, text, size = 24, color = "rgba(255,255,255,0.72)", latin, style }) => {
  const s = useSpringAt(at + 0.1, { damping: 20, stiffness: 200 }); // L3: type follows its plate
  return (
    <span
      data-audit="label"
      style={{
        ...(latin ? { fontFamily: inter, letterSpacing: "0.09em", lineHeight: 1.2 } : devaBlock),
        display: "inline-block",
        fontSize: size,
        fontWeight: 600,
        color,
        opacity: s,
        transform: `translateY(${((1 - s) * 9).toFixed(2)}px)`,
        whiteSpace: "pre",
        ...style,
      }}
    >
      {text}
    </span>
  );
};

// ── ANNOTATION EVENTS (research 01 §E) ──────────────────────────────────────
/** E1 — the highlight sweep. A marker stroke, Multiply, wiped L→R. Irregular ends sell it. */
export const Highlight: React.FC<{
  at: number;
  color?: string;
  dur?: number;
  style?: React.CSSProperties;
}> = ({ at, color = "#F5C518", dur = 0.34, style }) => {
  const w = useKeys([[at, 0], [at + dur, 1]], Easing.out(Easing.quad));
  return (
    <span
      data-audit="highlight"
      style={{
        position: "absolute",
        inset: "8% -1.5% 4% -1.5%",
        background: color,
        mixBlendMode: "multiply",
        transformOrigin: "0% 50%",
        transform: `scaleX(${w.toFixed(3)})`,
        borderRadius: "3px 9px 4px 8px",
        zIndex: -1,
        ...style,
      }}
    />
  );
};

/** E2 — circle-scribble callout. Hand-wobbled ellipse, drawn on. */
export const Scribble: React.FC<{
  at: number;
  w: number;
  h: number;
  color?: string;
  dur?: number;
  seed?: number;
  strokeWidth?: number;
  style?: React.CSSProperties;
}> = ({ at, w, h, color = "#E0523C", dur = 0.4, seed = 3, strokeWidth = 5, style }) => {
  const p = useKeys([[at, 0], [at + dur, 1]], Easing.out(Easing.cubic));
  const pts: string[] = [];
  const N = 44;
  const turns = 1.12;
  for (let i = 0; i <= N; i++) {
    const a = (i / N) * Math.PI * 2 * turns - Math.PI / 2;
    const wob = 1 + (random(`${seed}-${i}`) - 0.5) * 0.085;
    pts.push(`${(w / 2 + Math.cos(a) * (w / 2 - 6) * wob).toFixed(1)},${(h / 2 + Math.sin(a) * (h / 2 - 6) * wob).toFixed(1)}`);
  }
  const d = `M ${pts.join(" L ")}`;
  const len = (w + h) * 1.9;
  return (
    <svg data-audit="scribble" width={w} height={h} style={{ position: "absolute", overflow: "visible", ...style }}>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={len}
        strokeDashoffset={len * (1 - p)}
      />
    </svg>
  );
};

/** E-family — a rule/underline/connector that draws on. */
export const DrawLine: React.FC<{
  at: number;
  d: string;
  len: number;
  color?: string;
  dur?: number;
  strokeWidth?: number;
  dashed?: boolean;
  style?: React.CSSProperties;
}> = ({ at, d, len, color = "#F5C518", dur = 0.36, strokeWidth = 4, dashed, style }) => {
  const p = useKeys([[at, 0], [at + dur, 1]], Easing.out(Easing.cubic));
  return (
    <svg data-audit="drawline" style={{ position: "absolute", inset: 0, overflow: "visible", ...style }}>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={dashed ? `10 12` : len}
        strokeDashoffset={dashed ? 0 : len * (1 - p)}
        style={dashed ? { clipPath: `inset(0 ${(100 - p * 100).toFixed(1)}% 0 0)` } : undefined}
      />
    </svg>
  );
};

// ── L2 · MATERIAL GROUND ────────────────────────────────────────────────────
/**
 * Film grain. Reseeded every stepped frame so it CRAWLS like real grain instead of sitting
 * still like a texture (research 03 §6 — feTurbulence is static unless you drive the seed).
 * ⛔ Inline <svg>, never `background-image:` — that property is not gated by delayRender()
 * and can render empty (research 05, Remotion flickering doc).
 */
export const Grain: React.FC<{ opacity?: number; freq?: number }> = ({
  opacity = 0.13,
  freq = 0.82,
}) => {
  const frame = useStep();
  const seed = (frame % 7) * 131;
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity, mixBlendMode: "overlay" }}>
      <svg width="100%" height="100%" preserveAspectRatio="none">
        <filter id={`grain-${seed}`}>
          <feTurbulence type="fractalNoise" baseFrequency={freq} numOctaves={3} seed={seed} />
        </filter>
        <rect width="100%" height="100%" filter={`url(#grain-${seed})`} />
      </svg>
    </AbsoluteFill>
  );
};

/** Vignette — cheap, and it stops any frame reading as a flat plane. */
export const Vignette: React.FC<{ strength?: number; color?: string }> = ({
  strength = 0.55,
  color = "0,0,0",
}) => (
  <AbsoluteFill
    style={{
      pointerEvents: "none",
      background: `radial-gradient(120% 90% at 50% 45%, rgba(${color},0) 38%, rgba(${color},${strength}) 100%)`,
    }}
  />
);

/** Light sweep — a soft raking bar that crosses once. Sells a flat plate as lit. */
export const Sweep: React.FC<{ at: number; dur?: number; angle?: number; opacity?: number }> = ({
  at,
  dur = 1.1,
  angle = 104,
  opacity = 0.3,
}) => {
  const p = useKeys([[at, -0.4], [at + dur, 1.4]], Easing.inOut(Easing.quad), true);
  return (
    <AbsoluteFill
      style={{
        pointerEvents: "none",
        opacity,
        background: `linear-gradient(${angle}deg, transparent ${(p * 100 - 18).toFixed(1)}%, rgba(255,244,214,0.9) ${(p * 100).toFixed(1)}%, transparent ${(p * 100 + 18).toFixed(1)}%)`,
        mixBlendMode: "screen",
      }}
    />
  );
};

// ── CSS 3D PARALLAX (research 02 §5) ────────────────────────────────────────
/**
 * Depth3D — real perspective parallax from depth-sliced stills. NOT a Ken Burns zoom:
 * the camera translates in Z, so near layers grow faster than far ones. That difference IS
 * the 3D.
 *
 *   scale = (P + Z) / P            restores a pushed-back layer to its original screen size
 *   dolly  = translateZ(camZ) on the STAGE, never scale() on the frame
 *
 * ⛔ THE FLATTENING BUG: any `filter`, `opacity < 1`, `overflow: hidden`, `mask` or
 * `mix-blend-mode` on an element BETWEEN the perspective container and a layer destroys
 * `preserve-3d`. Per-layer blur/tint therefore goes on the layer's INNER child, never on the
 * transformed div. Atmosphere planes are siblings inside the stage, not wrappers.
 *
 * P ≈ 1.5-3× frame width reads as a long lens and hides the fact that the layers are flat cards.
 */
export type Layer3D = {
  src: string;
  z: number;
  /** px of blur — atmospheric perspective. Applied to the inner img, never the transform. */
  blur?: number;
  /** 0-1 haze wash toward `hazeColor`, painted as an inner overlay. */
  haze?: number;
  /** extra CSS filter functions appended after blur — e.g. "contrast(1.3) saturate(0.7)". */
  grade?: string;
  /** extra parallax nudge, in px, for hand-tuning a slice */
  dx?: number;
  dy?: number;
};

export const Depth3D: React.FC<{
  layers: Layer3D[];
  /** [second, camX, camY, camZ] — camZ is the dolly. Guard P - camZ + z > 0. */
  cam: [number, number, number, number][];
  P?: number;
  hazeColor?: string;
  /**
   * The over-render factor the slices were exported at (depth_slice.py --pad). The layer is
   * drawn `pad`× the frame and centred, so the padded ring sits OUTSIDE the visible frame and
   * gives the camera material to move into.
   * ⛔ Leaving this at 1 while the PNGs carry padding shows the edge-extended ring as streaks
   * along all four borders — that is a real defect, not grain.
   */
  pad?: number;
  children?: React.ReactNode;
}> = ({ layers, cam, P = 3400, hazeColor = "180,196,214", pad = 1, children }) => {
  const { fps, width, height } = useVideoConfig();
  const frame = useCurrentFrame(); // camera stays smooth
  const secs = cam.map((c) => Math.round(c[0] * fps));
  const pick = (i: 1 | 2 | 3) =>
    interpolate(frame, secs, cam.map((c) => c[i]), {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.bezier(0.62, 0, 0.3, 1),
    });
  const camX = pick(1);
  const camY = pick(2);
  const camZ = pick(3);

  return (
    <AbsoluteFill style={{ overflow: "hidden", backgroundColor: "#05070B" }}>
      <AbsoluteFill style={{ perspective: `${P}px`, perspectiveOrigin: "50% 50%" }}>
        <AbsoluteFill
          style={{
            transformStyle: "preserve-3d",
            transform: `translate3d(${camX.toFixed(2)}px, ${camY.toFixed(2)}px, ${camZ.toFixed(2)}px)`,
          }}
        >
          {layers.map((l) => {
            const s = (P + l.z) / P;
            const LW = width * pad;
            const LH = height * pad;
            return (
              <div
                key={l.src}
                style={{
                  position: "absolute",
                  left: -(LW - width) / 2,
                  top: -(LH - height) / 2,
                  width: LW,
                  height: LH,
                  transform: `translate3d(${l.dx ?? 0}px, ${l.dy ?? 0}px, ${-l.z}px) scale(${s.toFixed(4)})`,
                  transformStyle: "preserve-3d",
                  backfaceVisibility: "hidden",
                }}
              >
                {/* inner child carries the finishes — putting them above would flatten 3D */}
                <img
                  src={l.src}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    filter:
                      [l.blur ? `blur(${l.blur}px)` : "", l.grade ?? ""].filter(Boolean).join(" ") || undefined,
                    display: "block",
                  }}
                />
                {l.haze ? (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `rgba(${hazeColor},${l.haze})`,
                    }}
                  />
                ) : null}
              </div>
            );
          })}
          {children}
        </AbsoluteFill>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

/** Fog plane — sits BETWEEN depth layers as a sibling in the stage. Highest impact per byte. */
export const Fog: React.FC<{ z: number; color?: string; opacity?: number; P?: number }> = ({
  z,
  color = "176,192,210",
  opacity = 0.3,
  P = 3400,
}) => {
  const { width, height } = useVideoConfig();
  const s = (P + z) / P;
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width,
        height,
        transform: `translateZ(${-z}px) scale(${(s * 1.3).toFixed(4)})`,
        transformStyle: "preserve-3d",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(to top, rgba(${color},${opacity}) 0%, rgba(${color},${opacity * 0.45}) 42%, rgba(${color},0) 78%)`,
        }}
      />
    </div>
  );
};

/** Volumetric shafts. Screen-blended, masked to a cone. Keep the blur radius small on CPU. */
export const GodRays: React.FC<{
  x?: string;
  y?: string;
  angle?: number;
  opacity?: number;
  color?: string;
}> = ({ x = "62%", y = "-8%", angle = 16, opacity = 0.3, color = "255,236,198" }) => (
  <AbsoluteFill style={{ pointerEvents: "none", mixBlendMode: "screen", opacity }}>
    <div
      style={{
        position: "absolute",
        inset: "-30%",
        transform: `rotate(${angle}deg)`,
        background: `repeating-linear-gradient(96deg, rgba(${color},0) 0px, rgba(${color},0.5) 22px, rgba(${color},0) 74px, rgba(${color},0) 130px)`,
        WebkitMaskImage: `radial-gradient(58% 84% at ${x} ${y}, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0) 82%)`,
        maskImage: `radial-gradient(58% 84% at ${x} ${y}, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0) 82%)`,
        filter: "blur(7px)",
      }}
    />
  </AbsoluteFill>
);

// ── palette ─────────────────────────────────────────────────────────────────
export const PAL = {
  ink: "#12151C",
  bone: "#F2E9D8",
  saffron: "#E08A2B",
  kumkum: "#C0392B",
  gold: "#D4A73A",
  amber: "#F0913A",
  marker: "#F5C518",
  teal: "#2E6E6B",
  deepBlue: "#101A2B",
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
